import React, { useState, useEffect } from 'react';
import { runDiagnostics } from '../utils/testUtils';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import Button from './Button';

const DiagnosticsPanel: React.FC = () => {
  const [results, setResults] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    runTests();
  }, []);

  const runTests = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const diagnosticResults = await runDiagnostics();
      setResults(diagnosticResults);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred running diagnostics');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="animate-spin text-orange-500" size={24} />
        <span className="ml-2">Running diagnostics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 rounded-lg">
        <h3 className="text-red-700 font-semibold mb-2">Diagnostic Error</h3>
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (!results) {
    return null;
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-semibold mb-6">System Diagnostics</h2>
      
      <div className="space-y-6">
        {/* Core Systems */}
        <section>
          <h3 className="text-lg font-medium mb-4">Core Systems</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <span>Database Connection</span>
              {results.databaseAccess ? (
                <CheckCircle className="text-green-500" size={20} />
              ) : (
                <XCircle className="text-red-500" size={20} />
              )}
            </div>
            
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <span>Supabase Connection</span>
              {results.supabaseConnection ? (
                <CheckCircle className="text-green-500" size={20} />
              ) : (
                <XCircle className="text-red-500" size={20} />
              )}
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <span>Email Functionality</span>
              {results.emailFunctionality ? (
                <CheckCircle className="text-green-500" size={20} />
              ) : (
                <XCircle className="text-red-500" size={20} />
              )}
            </div>
          </div>
        </section>

        {/* Test Questions */}
        <section>
          <h3 className="text-lg font-medium mb-4">Test Configuration</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <span>Questions Count</span>
              <span className="font-medium">30 Questions</span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <span>Archetypes</span>
              <span className="font-medium">7 Types</span>
            </div>
          </div>
        </section>

        {/* Errors */}
        {results.errors.length > 0 && (
          <section className="mt-6">
            <h3 className="text-lg font-medium text-red-600 mb-4">Errors Found</h3>
            <div className="bg-red-50 p-4 rounded-lg">
              <ul className="list-disc pl-5 space-y-2">
                {results.errors.map((error: string, index: number) => (
                  <li key={index} className="text-red-600">{error}</li>
                ))}
              </ul>
            </div>
          </section>
        )}

        <div className="flex justify-end mt-6">
          <Button onClick={runTests}>
            Run Tests Again
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DiagnosticsPanel;